import type { ReactNode } from 'react';
import { usePublicBrandingQuery } from '../branding/api.js';

/** The navy-blue brand panel shown alongside every auth screen (hidden
 * below `lg` — a phone gets the form alone, full width, per
 * docs/architecture craft.md's "no fake chrome on small screens" rule).
 * Shared by every Auth & Onboarding screen (design/orion-crm-design/
 * SCREENS.md) so the split-panel treatment stays pixel-identical across
 * Login, forgot/reset password, and invite acceptance rather than drifting
 * per-page. Colors are scoped to this component (arbitrary values, not the
 * shared `--color-night` token) — that token is the app's general dark-UI
 * surface (sidebar, command palette, a dozen other screens), and recoloring
 * it to navy would have re-themed all of those along with the login page. */
function BrandPanel() {
  const { data: branding } = usePublicBrandingQuery();
  const productName = branding?.productName || 'Orion';
  const headline = branding?.loginHeadline || 'Every relationship, in one clear line of sight.';
  const subtext =
    branding?.loginSubtext ||
    'Leads, deals and follow-ups for the whole OES team — organised, accountable and ready for the next conversation.';
  // The sidebar (app/shell.tsx) already prefers the dark-background logo
  // variant over the light one when both exist; this panel is equally dark
  // and was the one place still only ever reading `logoUrl` — any admin who
  // uploaded a light-background logo (dark text/mark) got it rendered
  // straight onto this dark panel, often unreadable or boxed-looking.
  const logoUrl = branding?.logoDarkUrl || branding?.logoUrl;

  return (
    <aside
      aria-hidden="true"
      className="relative hidden w-[440px] shrink-0 flex-col overflow-hidden bg-[#0B1733] p-14 lg:flex xl:w-150"
    >
      <svg
        viewBox="0 0 600 900"
        preserveAspectRatio="xMidYMid slice"
        className="pointer-events-none absolute inset-0 size-full opacity-90"
      >
        <g stroke="#223160" strokeWidth="1.2" strokeLinecap="round">
          <path d="M316 104 204 168" />
          <path d="M316 104 432 196" />
          <path d="M204 168 284 316" />
          <path d="M432 196 380 284" />
          <path d="M284 316 332 300 380 284" />
          <path d="M284 316 236 452" />
          <path d="M380 284 464 436" />
        </g>
        <g fill="#223160">
          <circle cx="84" cy="236" r="1.6" />
          <circle cx="132" cy="120" r="1.2" />
          <circle cx="516" cy="92" r="1.6" />
          <circle cx="548" cy="268" r="1.2" />
          <circle cx="116" cy="392" r="1.2" />
          <circle cx="520" cy="372" r="1.6" />
          <circle cx="160" cy="492" r="1.2" />
          <circle cx="408" cy="520" r="1.2" />
          <circle cx="556" cy="492" r="1.2" />
          <circle cx="252" cy="232" r="1.2" />
          <circle cx="456" cy="132" r="1.2" />
          <circle cx="72" cy="460" r="1.6" />
        </g>
        <g fill="#5C72B3">
          <circle cx="316" cy="104" r="2.4" />
          <circle cx="204" cy="168" r="3.4" />
          <circle cx="432" cy="196" r="3" />
          <circle cx="236" cy="452" r="2.8" />
          <circle cx="464" cy="436" r="3.4" />
        </g>
        <g fill="#FFFFFF">
          <circle cx="284" cy="316" r="12" fillOpacity="0.1" />
          <circle cx="332" cy="300" r="12" fillOpacity="0.1" />
          <circle cx="380" cy="284" r="12" fillOpacity="0.1" />
          <circle cx="284" cy="316" r="4" />
          <circle cx="332" cy="300" r="4" />
          <circle cx="380" cy="284" r="4" />
        </g>
      </svg>

      <div className="relative flex items-center gap-3">
        <span className="flex size-9 items-center justify-center overflow-hidden rounded-[10px] bg-white">
          {logoUrl ? (
            <img src={logoUrl} alt={productName} className="size-full object-contain p-1" />
          ) : (
            <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true">
              <circle cx="6.5" cy="16" r="2" fill="#0B1733" />
              <circle cx="12" cy="12" r="2" fill="#0B1733" />
              <circle cx="17.5" cy="8" r="2" fill="#0B1733" />
              <circle cx="5" cy="5" r="1.1" fill="#5C72B3" />
              <circle cx="19" cy="19" r="1.1" fill="#5C72B3" />
            </svg>
          )}
        </span>
        <span className="text-lg font-[650] tracking-[-0.01em] text-white">{productName}</span>
      </div>

      <div className="relative mt-auto flex max-w-115 flex-col gap-4">
        <h2 className="font-sans text-[48px] leading-13 tracking-[-0.01em] font-normal text-white">{headline}</h2>
        <p className="text-[15px] leading-6 text-[#C6CEE8]">{subtext}</p>
      </div>

      <div className="relative mt-12 flex items-center gap-2 text-xs text-[#8C9BCB]">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#8C9BCB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M12 3 4 6v6c0 5 3.5 8 8 9 4.5-1 8-4 8-9V6z" />
        </svg>
        OES Workspace · Secured with Microsoft Entra ID
      </div>
    </aside>
  );
}

export function AuthShell({ children }: { children: ReactNode }) {
  return (
    <div className="flex min-h-screen bg-ground">
      <BrandPanel />
      <main className="flex min-w-0 flex-1 flex-col">
        <div className="flex justify-end px-12 pt-8 text-[13px] text-text-muted">
          Having trouble?
          <a href="mailto:support@oes.com" className="ml-1 font-semibold text-ink hover:underline">
            Get help
          </a>
        </div>

        <div className="flex flex-1 items-center justify-center px-4 py-10">{children}</div>

        <footer className="flex flex-col items-center gap-2 px-12 pb-8 text-xs text-text-muted sm:flex-row sm:justify-between">
          <span>© 2026 OES · Orion CRM v1.0</span>
          <nav aria-label="Legal" className="flex items-center gap-2">
            <a href="#" className="text-text-secondary hover:text-ink">
              Privacy
            </a>
            <span aria-hidden="true">·</span>
            <a href="#" className="text-text-secondary hover:text-ink">
              Terms
            </a>
            <span aria-hidden="true">·</span>
            <a href="#" className="text-text-secondary hover:text-ink">
              Status
            </a>
          </nav>
        </footer>
      </main>
    </div>
  );
}

/** The white step-card used by every multi-step auth flow (forgot/reset
 * password, accept-invite) — matches the panel styling in
 * design/orion-crm-design/rendered-html/ForgotPassword.html exactly
 * (radius 12px, border, shadow, 20px padding, 16px gap). */
export function AuthStepCard({ children }: { children: ReactNode }) {
  return (
    <div className="flex w-full max-w-115 flex-col gap-4 rounded-xl border border-border bg-surface p-5 shadow-[0_1px_2px_rgba(22,24,29,0.05)]">
      {children}
    </div>
  );
}

export function AuthStepEyebrow({ step, of, label }: { step: number; of: number; label: string }) {
  return (
    <span className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-primary-text">
      <span className="flex size-5 items-center justify-center rounded-full bg-primary text-[11px] text-white">{step}</span>
      Step {step} of {of} · {label}
    </span>
  );
}
